import type { AuthProvider } from "@refinedev/core";
import { supabase } from "./supabase";

/**
 * Refine authProvider that wraps Supabase Auth.
 *
 * Flow:
 *  - login()        → supabase.auth.signInWithPassword
 *  - logout()       → supabase.auth.signOut
 *  - check()        → supabase.auth.getSession (drives <Authenticated/>)
 *  - getIdentity()  → supabase.auth.getUser
 *  - onError()      → 401 / 403 redirects to /login
 *
 * Public sign-up is DISABLED — accounts are created by admins via the
 * `invite-user` Edge Function (see supabase/functions/invite-user/).
 */
export const authProvider: AuthProvider = {
  login: async ({ email, password }: { email: string; password: string }) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return {
        success: false,
        error: {
          name: "LoginError",
          message: error.message,
        },
      };
    }

    if (data?.session) {
      return { success: true, redirectTo: "/dashboard" };
    }

    return {
      success: false,
      error: { name: "LoginError", message: "No session returned" },
    };
  },

  logout: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      return {
        success: false,
        error: { name: "LogoutError", message: error.message },
      };
    }
    return { success: true, redirectTo: "/login" };
  },

  check: async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session) {
      return { authenticated: false, redirectTo: "/login" };
    }
    return { authenticated: true };
  },

  getPermissions: async () => {
    // Phase 6 Wave 2 will return staff.user_roles. For Wave 1 the layout
    // doesn't gate by role yet.
    return null;
  },

  getIdentity: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) return null;
    return {
      id: data.user.id,
      email: data.user.email,
      name: data.user.user_metadata?.display_name ?? data.user.email,
    };
  },

  onError: async (error) => {
    const status = (error as { status?: number } | undefined)?.status;
    if (status === 401 || status === 403) {
      return { logout: true, redirectTo: "/login" };
    }
    return {};
  },
};
